/**
 * 결제 결과 페이지
 *
 * Bloomin 3DS 결제 완료 후 리다이렉트되는 페이지입니다.
 * URL 파라미터에서 결제 결과를 확인하고 상태를 표시합니다.
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X, Loader2, ArrowLeft, Receipt, RefreshCcw, Copy, CheckCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { usePaymentApi } from '@/hooks/usePaymentApi';
import { ROUTES, getReceiptPath, getRestaurantTablePath } from '@/config/routes';
import type { PaymentStatus } from '@/lib/api/types';
import { getSavedGuestPassword } from '@/utils/password';
import { useAuth } from '@/contexts/AuthContext';
import { useCartActions } from '@/contexts/CartContext';
import { useLanguage } from '@/contexts/AppContext';
import { getTableSessionFromStorage, getTableSessionReturnContext } from '@/lib/tableContext';
import type { GuestPushContext } from '@/lib/push';
import { PushOptInBanner } from '@/components/checkout/PushOptInBanner';
import { checkoutTranslations } from '@/i18n/checkout';
import { a11yTranslations } from '@/i18n/a11y';
import { paymentResult3dsTranslations } from '@/i18n/paymentResult3ds';
import { paymentResultNavTranslations } from '@/i18n/paymentResultNav';
import { logger } from '@/utils/logger';
import type { Language, Locale } from '@/config/locales';
import { activeOrderDisplayNumber } from '@/utils/orderDisplay';
import { GuestPointsIncentive } from '@/components/points/GuestPointsIncentive';
import {
  splitPaymentApi,
  type MembershipRewardPreview,
  type SessionBillReceipt,
} from '@/lib/api/split-payment';
import {
  clearPendingBillReceiptIfMatches,
  clearSessionChargeResult,
  readSessionChargeResult,
  type PendingBillReceiptContext,
} from '@/lib/billReceiptContext';

interface PaymentResultStrings {
  loadingHeader: string;
  loadingTitle: string;
  loadingDescription: string;
  successHeader: string;
  successTitle: string;
  successDescription: string;
  pendingHeader: string;
  pendingTitle: string;
  pendingDescription: string;
  cancelledHeader: string;
  cancelledTitle: string;
  cancelledDescription: string;
  failedHeader: string;
  failedTitle: string;
  failedRetryHint: string;
  invalidPaymentInfo: string;
  paymentFailedDefault: string;
  paymentStatusCheckFailed: string;
  genericPaymentError: string;
  orderNumber: string;
  paymentMethod: string;
  paymentAmount: string;
  cardPayment: string;
  cashPayment: string;
  bankTransfer: string;
  guestOrderCode: string;
  guestOrderCodeDescription: string;
  copy: string;
  copied: string;
  viewReceipt: string;
  goHome: string;
  recheckStatus: string;
  retry: string;
  payConfirmAction: string;
  payCompletedAction: string;
  payFailedAction: string;
  payCancelledAction: string;
  payPendingAction: string;
}

// #522 — Locale 단일 출처 정합 (15언어 전수).
type PaymentResultTranslations =
  & Record<Language, PaymentResultStrings>
  & Record<Exclude<Locale, Language>, PaymentResultStrings>;

const RESULT_TRANSLATIONS: PaymentResultTranslations = {
  ko: {
    loadingHeader: '결제 확인 중',
    loadingTitle: '결제 결과를 확인하고 있습니다',
    loadingDescription: '잠시만 기다려 주세요...',
    successHeader: '결제 완료',
    successTitle: '결제가 완료되었습니다',
    successDescription: '주문이 정상적으로 접수되었습니다.',
    pendingHeader: '결제 대기중',
    pendingTitle: '결제 확인 대기중',
    pendingDescription: '매장에서 결제를 확인하고 있습니다. 잠시만 기다려 주세요.',
    cancelledHeader: '결제 취소',
    cancelledTitle: '결제가 취소되었습니다',
    cancelledDescription: '결제가 취소되었습니다. 다시 시도하시려면 홈으로 돌아가세요.',
    failedHeader: '결제 실패',
    failedTitle: '결제에 실패했습니다',
    failedRetryHint: '다시 시도하시거나 다른 결제 수단을 이용해 주세요.',
    invalidPaymentInfo: '결제 정보가 올바르지 않습니다.',
    paymentFailedDefault: '결제에 실패했습니다.',
    paymentStatusCheckFailed: '결제 상태를 확인할 수 없습니다.',
    genericPaymentError: '결제 처리 중 오류가 발생했습니다.',
    orderNumber: '주문 번호',
    paymentMethod: '결제 수단',
    paymentAmount: '결제 금액',
    cardPayment: '카드 결제',
    cashPayment: '현금 결제',
    bankTransfer: '계좌이체',
    guestOrderCode: '📋 주문 조회 코드',
    guestOrderCodeDescription: '주문 내역 조회 시 이 코드가 필요합니다. 저장해 두세요!',
    copy: '복사',
    copied: '복사됨',
    viewReceipt: '영수증 보기',
    goHome: '홈으로 돌아가기',
    recheckStatus: '상태 다시 확인',
    retry: '다시 시도',
    payConfirmAction: '결제 확인 후 동작',
    payCompletedAction: '결제 완료 후 동작',
    payFailedAction: '결제 실패 후 동작',
    payCancelledAction: '결제 취소 후 동작',
    payPendingAction: '결제 대기 중 동작',
  },
  es: {
    loadingHeader: 'Verificando pago',
    loadingTitle: 'Verificando el resultado del pago',
    loadingDescription: 'Por favor espera un momento...',
    successHeader: 'Pago completado',
    successTitle: 'El pago fue completado',
    successDescription: 'Tu pedido ha sido recibido correctamente.',
    pendingHeader: 'Pago pendiente',
    pendingTitle: 'Esperando confirmación de pago',
    pendingDescription: 'La tienda está confirmando el pago. Por favor espera un momento.',
    cancelledHeader: 'Pago cancelado',
    cancelledTitle: 'El pago fue cancelado',
    cancelledDescription: 'El pago fue cancelado. Vuelve al inicio para intentar de nuevo.',
    failedHeader: 'Pago fallido',
    failedTitle: 'No se pudo completar el pago',
    failedRetryHint: 'Intenta de nuevo o utiliza otro método de pago.',
    invalidPaymentInfo: 'La información de pago no es válida.',
    paymentFailedDefault: 'El pago no se pudo completar.',
    paymentStatusCheckFailed: 'No se pudo verificar el estado del pago.',
    genericPaymentError: 'Ocurrió un error al procesar el pago.',
    orderNumber: 'Número de pedido',
    paymentMethod: 'Método de pago',
    paymentAmount: 'Monto pagado',
    cardPayment: 'Pago con tarjeta',
    cashPayment: 'Pago en efectivo',
    bankTransfer: 'Transferencia bancaria',
    guestOrderCode: '📋 Código para consultar el pedido',
    guestOrderCodeDescription: 'Necesitarás este código para consultar tu pedido. ¡Guárdalo!',
    copy: 'Copiar',
    copied: 'Copiado',
    viewReceipt: 'Ver recibo',
    goHome: 'Volver al inicio',
    recheckStatus: 'Verificar estado de nuevo',
    retry: 'Reintentar',
    payConfirmAction: 'Acciones de verificación de pago',
    payCompletedAction: 'Acciones después del pago',
    payFailedAction: 'Acciones después del fallo de pago',
    payCancelledAction: 'Acciones después de la cancelación',
    payPendingAction: 'Acciones durante el pago pendiente',
  },
  en: {
    loadingHeader: 'Verifying payment',
    loadingTitle: 'Checking the payment result',
    loadingDescription: 'Please wait a moment...',
    successHeader: 'Payment completed',
    successTitle: 'Payment was completed',
    successDescription: 'Your order has been received successfully.',
    pendingHeader: 'Payment pending',
    pendingTitle: 'Waiting for payment confirmation',
    pendingDescription: 'The store is confirming the payment. Please wait a moment.',
    cancelledHeader: 'Payment cancelled',
    cancelledTitle: 'The payment was cancelled',
    cancelledDescription: 'The payment was cancelled. Go back to the home screen to try again.',
    failedHeader: 'Payment failed',
    failedTitle: 'Payment could not be completed',
    failedRetryHint: 'Please try again or use a different payment method.',
    invalidPaymentInfo: 'Payment information is invalid.',
    paymentFailedDefault: 'Payment could not be completed.',
    paymentStatusCheckFailed: 'Could not verify the payment status.',
    genericPaymentError: 'An error occurred while processing the payment.',
    orderNumber: 'Order number',
    paymentMethod: 'Payment method',
    paymentAmount: 'Amount paid',
    cardPayment: 'Card payment',
    cashPayment: 'Cash payment',
    bankTransfer: 'Bank transfer',
    guestOrderCode: '📋 Order lookup code',
    guestOrderCodeDescription: 'You will need this code to look up your order. Save it!',
    copy: 'Copy',
    copied: 'Copied',
    viewReceipt: 'View receipt',
    goHome: 'Back to home',
    recheckStatus: 'Recheck status',
    retry: 'Retry',
    payConfirmAction: 'Payment verification actions',
    payCompletedAction: 'Post-payment actions',
    payFailedAction: 'Post-failure actions',
    payCancelledAction: 'Post-cancellation actions',
    payPendingAction: 'Pending payment actions',
  },
  pt: {
    loadingHeader: 'Verificando pagamento',
    loadingTitle: 'Verificando o resultado do pagamento',
    loadingDescription: 'Aguarde um momento...',
    successHeader: 'Pagamento concluído',
    successTitle: 'O pagamento foi concluído',
    successDescription: 'Seu pedido foi recebido com sucesso.',
    pendingHeader: 'Pagamento pendente',
    pendingTitle: 'Aguardando confirmação do pagamento',
    pendingDescription: 'A loja está confirmando o pagamento. Aguarde um momento.',
    cancelledHeader: 'Pagamento cancelado',
    cancelledTitle: 'O pagamento foi cancelado',
    cancelledDescription: 'O pagamento foi cancelado. Volte ao início para tentar novamente.',
    failedHeader: 'Pagamento recusado',
    failedTitle: 'Não foi possível concluir o pagamento',
    failedRetryHint: 'Tente novamente ou use outro método de pagamento.',
    invalidPaymentInfo: 'As informações de pagamento não são válidas.',
    paymentFailedDefault: 'O pagamento não pôde ser concluído.',
    paymentStatusCheckFailed: 'Não foi possível verificar o status do pagamento.',
    genericPaymentError: 'Ocorreu um erro ao processar o pagamento.',
    orderNumber: 'Número do pedido',
    paymentMethod: 'Método de pagamento',
    paymentAmount: 'Valor pago',
    cardPayment: 'Pagamento com cartão',
    cashPayment: 'Pagamento em dinheiro',
    bankTransfer: 'Transferência bancária',
    guestOrderCode: '📋 Código de consulta do pedido',
    guestOrderCodeDescription: 'Você precisará deste código para consultar seu pedido. Guarde-o!',
    copy: 'Copiar',
    copied: 'Copiado',
    viewReceipt: 'Ver recibo',
    goHome: 'Voltar ao início',
    recheckStatus: 'Verificar status novamente',
    retry: 'Tentar novamente',
    payConfirmAction: 'Ações de verificação do pagamento',
    payCompletedAction: 'Ações após o pagamento',
    payFailedAction: 'Ações após a falha do pagamento',
    payCancelledAction: 'Ações após o cancelamento',
    payPendingAction: 'Ações durante o pagamento pendente',
  },
  fr: {
    loadingHeader: 'Vérification du paiement',
    loadingTitle: 'Vérification du résultat du paiement',
    loadingDescription: 'Veuillez patienter un instant...',
    successHeader: 'Paiement effectué',
    successTitle: 'Le paiement a été effectué',
    successDescription: 'Votre commande a bien été enregistrée.',
    pendingHeader: 'Paiement en attente',
    pendingTitle: 'En attente de la confirmation du paiement',
    pendingDescription: 'Le restaurant confirme le paiement. Veuillez patienter un instant.',
    cancelledHeader: 'Paiement annulé',
    cancelledTitle: 'Le paiement a été annulé',
    cancelledDescription: "Le paiement a été annulé. Retournez à l'accueil pour réessayer.",
    failedHeader: 'Échec du paiement',
    failedTitle: "Le paiement n'a pas pu être effectué",
    failedRetryHint: 'Veuillez réessayer ou utiliser un autre moyen de paiement.',
    invalidPaymentInfo: 'Les informations de paiement ne sont pas valides.',
    paymentFailedDefault: "Le paiement n'a pas pu être effectué.",
    paymentStatusCheckFailed: "Impossible de vérifier l'état du paiement.",
    genericPaymentError: "Une erreur s'est produite lors du traitement du paiement.",
    orderNumber: 'Numéro de commande',
    paymentMethod: 'Moyen de paiement',
    paymentAmount: 'Montant payé',
    cardPayment: 'Paiement par carte',
    cashPayment: 'Paiement en espèces',
    bankTransfer: 'Virement bancaire',
    guestOrderCode: '📋 Code de consultation de commande',
    guestOrderCodeDescription: 'Vous aurez besoin de ce code pour consulter votre commande. Conservez-le !',
    copy: 'Copier',
    copied: 'Copié',
    viewReceipt: 'Voir le reçu',
    goHome: "Retour à l'accueil",
    recheckStatus: "Vérifier à nouveau l'état",
    retry: 'Réessayer',
    payConfirmAction: 'Actions de vérification du paiement',
    payCompletedAction: 'Actions après le paiement',
    payFailedAction: "Actions après l'échec du paiement",
    payCancelledAction: "Actions après l'annulation",
    payPendingAction: 'Actions pendant le paiement en attente',
  },
  zh: {
    loadingHeader: '正在确认支付',
    loadingTitle: '正在确认支付结果',
    loadingDescription: '请稍候...',
    successHeader: '支付完成',
    successTitle: '支付已完成',
    successDescription: '您的订单已成功提交。',
    pendingHeader: '支付待确认',
    pendingTitle: '等待支付确认',
    pendingDescription: '商家正在确认支付，请稍候。',
    cancelledHeader: '支付已取消',
    cancelledTitle: '支付已被取消',
    cancelledDescription: '支付已取消。请返回首页重新尝试。',
    failedHeader: '支付失败',
    failedTitle: '支付未能完成',
    failedRetryHint: '请重试或使用其他支付方式。',
    invalidPaymentInfo: '支付信息无效。',
    paymentFailedDefault: '支付未能完成。',
    paymentStatusCheckFailed: '无法确认支付状态。',
    genericPaymentError: '处理支付时发生错误。',
    orderNumber: '订单号',
    paymentMethod: '支付方式',
    paymentAmount: '支付金额',
    cardPayment: '银行卡支付',
    cashPayment: '现金支付',
    bankTransfer: '银行转账',
    guestOrderCode: '📋 订单查询码',
    guestOrderCodeDescription: '查询订单时需要此查询码，请妥善保存！',
    copy: '复制',
    copied: '已复制',
    viewReceipt: '查看收据',
    goHome: '返回首页',
    recheckStatus: '重新确认状态',
    retry: '重试',
    payConfirmAction: '支付确认后的操作',
    payCompletedAction: '支付完成后的操作',
    payFailedAction: '支付失败后的操作',
    payCancelledAction: '支付取消后的操作',
    payPendingAction: '支付待确认时的操作',
  },
  ja: {
    loadingHeader: 'お支払い確認中',
    loadingTitle: 'お支払い結果を確認しています',
    loadingDescription: '少々お待ちください...',
    successHeader: 'お支払い完了',
    successTitle: 'お支払いが完了しました',
    successDescription: 'ご注文は正常に受け付けられました。',
    pendingHeader: 'お支払い待ち',
    pendingTitle: 'お支払い確認待ちです',
    pendingDescription: '店舗がお支払いを確認しています。少々お待ちください。',
    cancelledHeader: 'お支払いキャンセル',
    cancelledTitle: 'お支払いがキャンセルされました',
    cancelledDescription: 'お支払いがキャンセルされました。再試行するにはホームへお戻りください。',
    failedHeader: 'お支払い失敗',
    failedTitle: 'お支払いに失敗しました',
    failedRetryHint: 'もう一度お試しいただくか、別のお支払い方法をご利用ください。',
    invalidPaymentInfo: 'お支払い情報が正しくありません。',
    paymentFailedDefault: 'お支払いを完了できませんでした。',
    paymentStatusCheckFailed: 'お支払い状況を確認できません。',
    genericPaymentError: 'お支払い処理中にエラーが発生しました。',
    orderNumber: '注文番号',
    paymentMethod: 'お支払い方法',
    paymentAmount: 'お支払い金額',
    cardPayment: 'カード払い',
    cashPayment: '現金払い',
    bankTransfer: '銀行振込',
    guestOrderCode: '📋 注文照会コード',
    guestOrderCodeDescription: '注文履歴の照会にこのコードが必要です。保存しておいてください！',
    copy: 'コピー',
    copied: 'コピーしました',
    viewReceipt: 'レシートを見る',
    goHome: 'ホームに戻る',
    recheckStatus: '状態を再確認',
    retry: '再試行',
    payConfirmAction: 'お支払い確認後の操作',
    payCompletedAction: 'お支払い完了後の操作',
    payFailedAction: 'お支払い失敗後の操作',
    payCancelledAction: 'お支払いキャンセル後の操作',
    payPendingAction: 'お支払い待ちの間の操作',
  },
  hi: {
    loadingHeader: 'भुगतान की जाँच हो रही है',
    loadingTitle: 'भुगतान का परिणाम जाँचा जा रहा है',
    loadingDescription: 'कृपया थोड़ी प्रतीक्षा करें...',
    successHeader: 'भुगतान पूर्ण',
    successTitle: 'भुगतान पूरा हो गया',
    successDescription: 'आपका ऑर्डर सफलतापूर्वक प्राप्त हो गया है।',
    pendingHeader: 'भुगतान लंबित',
    pendingTitle: 'भुगतान की पुष्टि की प्रतीक्षा में',
    pendingDescription: 'दुकान भुगतान की पुष्टि कर रही है। कृपया थोड़ी प्रतीक्षा करें।',
    cancelledHeader: 'भुगतान रद्द',
    cancelledTitle: 'भुगतान रद्द कर दिया गया',
    cancelledDescription: 'भुगतान रद्द कर दिया गया। फिर से प्रयास करने के लिए होम पर वापस जाएँ।',
    failedHeader: 'भुगतान विफल',
    failedTitle: 'भुगतान पूरा नहीं हो सका',
    failedRetryHint: 'कृपया फिर से प्रयास करें या कोई अन्य भुगतान विधि उपयोग करें।',
    invalidPaymentInfo: 'भुगतान की जानकारी मान्य नहीं है।',
    paymentFailedDefault: 'भुगतान पूरा नहीं हो सका।',
    paymentStatusCheckFailed: 'भुगतान की स्थिति की जाँच नहीं हो सकी।',
    genericPaymentError: 'भुगतान संसाधित करते समय एक त्रुटि हुई।',
    orderNumber: 'ऑर्डर नंबर',
    paymentMethod: 'भुगतान विधि',
    paymentAmount: 'भुगतान राशि',
    cardPayment: 'कार्ड से भुगतान',
    cashPayment: 'नकद भुगतान',
    bankTransfer: 'बैंक हस्तांतरण',
    guestOrderCode: '📋 ऑर्डर खोज कोड',
    guestOrderCodeDescription: 'अपना ऑर्डर देखने के लिए आपको इस कोड की आवश्यकता होगी। इसे सहेज लें!',
    copy: 'कॉपी करें',
    copied: 'कॉपी हो गया',
    viewReceipt: 'रसीद देखें',
    goHome: 'होम पर वापस जाएँ',
    recheckStatus: 'स्थिति फिर जाँचें',
    retry: 'पुनः प्रयास करें',
    payConfirmAction: 'भुगतान सत्यापन के बाद की कार्रवाइयाँ',
    payCompletedAction: 'भुगतान पूर्ण होने के बाद की कार्रवाइयाँ',
    payFailedAction: 'भुगतान विफल होने के बाद की कार्रवाइयाँ',
    payCancelledAction: 'भुगतान रद्द होने के बाद की कार्रवाइयाँ',
    payPendingAction: 'लंबित भुगतान के दौरान की कार्रवाइयाँ',
  },
  de: {
    loadingHeader: 'Zahlung wird überprüft',
    loadingTitle: 'Das Zahlungsergebnis wird überprüft',
    loadingDescription: 'Bitte warten Sie einen Moment...',
    successHeader: 'Zahlung abgeschlossen',
    successTitle: 'Die Zahlung wurde abgeschlossen',
    successDescription: 'Ihre Bestellung ist erfolgreich eingegangen.',
    pendingHeader: 'Zahlung ausstehend',
    pendingTitle: 'Warten auf Zahlungsbestätigung',
    pendingDescription: 'Das Restaurant bestätigt die Zahlung. Bitte warten Sie einen Moment.',
    cancelledHeader: 'Zahlung storniert',
    cancelledTitle: 'Die Zahlung wurde storniert',
    cancelledDescription: 'Die Zahlung wurde storniert. Kehren Sie zur Startseite zurück, um es erneut zu versuchen.',
    failedHeader: 'Zahlung fehlgeschlagen',
    failedTitle: 'Die Zahlung konnte nicht abgeschlossen werden',
    failedRetryHint: 'Bitte versuchen Sie es erneut oder verwenden Sie eine andere Zahlungsmethode.',
    invalidPaymentInfo: 'Die Zahlungsinformationen sind ungültig.',
    paymentFailedDefault: 'Die Zahlung konnte nicht abgeschlossen werden.',
    paymentStatusCheckFailed: 'Der Zahlungsstatus konnte nicht überprüft werden.',
    genericPaymentError: 'Bei der Verarbeitung der Zahlung ist ein Fehler aufgetreten.',
    orderNumber: 'Bestellnummer',
    paymentMethod: 'Zahlungsmethode',
    paymentAmount: 'Gezahlter Betrag',
    cardPayment: 'Kartenzahlung',
    cashPayment: 'Barzahlung',
    bankTransfer: 'Banküberweisung',
    guestOrderCode: '📋 Bestellabfrage-Code',
    guestOrderCodeDescription: 'Sie benötigen diesen Code, um Ihre Bestellung abzufragen. Bewahren Sie ihn auf!',
    copy: 'Kopieren',
    copied: 'Kopiert',
    viewReceipt: 'Beleg ansehen',
    goHome: 'Zur Startseite',
    recheckStatus: 'Status erneut prüfen',
    retry: 'Erneut versuchen',
    payConfirmAction: 'Aktionen nach der Zahlungsprüfung',
    payCompletedAction: 'Aktionen nach der Zahlung',
    payFailedAction: 'Aktionen nach fehlgeschlagener Zahlung',
    payCancelledAction: 'Aktionen nach der Stornierung',
    payPendingAction: 'Aktionen während ausstehender Zahlung',
  },
  it: {
    loadingHeader: 'Verifica del pagamento',
    loadingTitle: 'Verifica del risultato del pagamento',
    loadingDescription: 'Attendi un momento...',
    successHeader: 'Pagamento completato',
    successTitle: 'Il pagamento è stato completato',
    successDescription: 'Il tuo ordine è stato ricevuto correttamente.',
    pendingHeader: 'Pagamento in attesa',
    pendingTitle: 'In attesa della conferma del pagamento',
    pendingDescription: 'Il locale sta confermando il pagamento. Attendi un momento.',
    cancelledHeader: 'Pagamento annullato',
    cancelledTitle: 'Il pagamento è stato annullato',
    cancelledDescription: 'Il pagamento è stato annullato. Torna alla home per riprovare.',
    failedHeader: 'Pagamento non riuscito',
    failedTitle: 'Non è stato possibile completare il pagamento',
    failedRetryHint: 'Riprova o utilizza un altro metodo di pagamento.',
    invalidPaymentInfo: 'Le informazioni di pagamento non sono valide.',
    paymentFailedDefault: 'Il pagamento non è stato completato.',
    paymentStatusCheckFailed: 'Impossibile verificare lo stato del pagamento.',
    genericPaymentError: "Si è verificato un errore durante l'elaborazione del pagamento.",
    orderNumber: "Numero d'ordine",
    paymentMethod: 'Metodo di pagamento',
    paymentAmount: 'Importo pagato',
    cardPayment: 'Pagamento con carta',
    cashPayment: 'Pagamento in contanti',
    bankTransfer: 'Bonifico bancario',
    guestOrderCode: "📋 Codice di consultazione dell'ordine",
    guestOrderCodeDescription: 'Avrai bisogno di questo codice per consultare il tuo ordine. Conservalo!',
    copy: 'Copia',
    copied: 'Copiato',
    viewReceipt: 'Vedi ricevuta',
    goHome: 'Torna alla home',
    recheckStatus: 'Verifica di nuovo lo stato',
    retry: 'Riprova',
    payConfirmAction: 'Azioni di verifica del pagamento',
    payCompletedAction: 'Azioni dopo il pagamento',
    payFailedAction: 'Azioni dopo il pagamento non riuscito',
    payCancelledAction: "Azioni dopo l'annullamento",
    payPendingAction: 'Azioni durante il pagamento in attesa',
  },
  ru: {
    loadingHeader: 'Проверка платежа',
    loadingTitle: 'Проверяем результат платежа',
    loadingDescription: 'Пожалуйста, подождите...',
    successHeader: 'Платёж завершён',
    successTitle: 'Платёж успешно завершён',
    successDescription: 'Ваш заказ успешно принят.',
    pendingHeader: 'Платёж в ожидании',
    pendingTitle: 'Ожидание подтверждения платежа',
    pendingDescription: 'Заведение подтверждает платёж. Пожалуйста, подождите.',
    cancelledHeader: 'Платёж отменён',
    cancelledTitle: 'Платёж был отменён',
    cancelledDescription: 'Платёж был отменён. Вернитесь на главную, чтобы попробовать снова.',
    failedHeader: 'Платёж не выполнен',
    failedTitle: 'Не удалось завершить платёж',
    failedRetryHint: 'Попробуйте ещё раз или используйте другой способ оплаты.',
    invalidPaymentInfo: 'Платёжные данные недействительны.',
    paymentFailedDefault: 'Не удалось завершить платёж.',
    paymentStatusCheckFailed: 'Не удалось проверить статус платежа.',
    genericPaymentError: 'При обработке платежа произошла ошибка.',
    orderNumber: 'Номер заказа',
    paymentMethod: 'Способ оплаты',
    paymentAmount: 'Сумма оплаты',
    cardPayment: 'Оплата картой',
    cashPayment: 'Оплата наличными',
    bankTransfer: 'Банковский перевод',
    guestOrderCode: '📋 Код для просмотра заказа',
    guestOrderCodeDescription: 'Этот код понадобится для просмотра вашего заказа. Сохраните его!',
    copy: 'Копировать',
    copied: 'Скопировано',
    viewReceipt: 'Посмотреть чек',
    goHome: 'Вернуться на главную',
    recheckStatus: 'Проверить статус ещё раз',
    retry: 'Повторить',
    payConfirmAction: 'Действия после проверки платежа',
    payCompletedAction: 'Действия после оплаты',
    payFailedAction: 'Действия после неудачной оплаты',
    payCancelledAction: 'Действия после отмены',
    payPendingAction: 'Действия во время ожидания платежа',
  },
  ar: {
    loadingHeader: 'جارٍ التحقق من الدفع',
    loadingTitle: 'جارٍ التحقق من نتيجة الدفع',
    loadingDescription: 'يرجى الانتظار قليلاً...',
    successHeader: 'اكتمل الدفع',
    successTitle: 'تم إتمام الدفع',
    successDescription: 'تم استلام طلبك بنجاح.',
    pendingHeader: 'الدفع قيد الانتظار',
    pendingTitle: 'في انتظار تأكيد الدفع',
    pendingDescription: 'يقوم المتجر بتأكيد الدفع. يرجى الانتظار قليلاً.',
    cancelledHeader: 'تم إلغاء الدفع',
    cancelledTitle: 'تم إلغاء الدفع',
    cancelledDescription: 'تم إلغاء الدفع. عُد إلى الصفحة الرئيسية للمحاولة مرة أخرى.',
    failedHeader: 'فشل الدفع',
    failedTitle: 'تعذّر إتمام الدفع',
    failedRetryHint: 'يرجى المحاولة مرة أخرى أو استخدام وسيلة دفع أخرى.',
    invalidPaymentInfo: 'معلومات الدفع غير صالحة.',
    paymentFailedDefault: 'تعذّر إتمام الدفع.',
    paymentStatusCheckFailed: 'تعذّر التحقق من حالة الدفع.',
    genericPaymentError: 'حدث خطأ أثناء معالجة الدفع.',
    orderNumber: 'رقم الطلب',
    paymentMethod: 'وسيلة الدفع',
    paymentAmount: 'المبلغ المدفوع',
    cardPayment: 'الدفع بالبطاقة',
    cashPayment: 'الدفع نقدًا',
    bankTransfer: 'تحويل بنكي',
    guestOrderCode: '📋 رمز الاستعلام عن الطلب',
    guestOrderCodeDescription: 'ستحتاج إلى هذا الرمز للاستعلام عن طلبك. احتفظ به!',
    copy: 'نسخ',
    copied: 'تم النسخ',
    viewReceipt: 'عرض الإيصال',
    goHome: 'العودة إلى الرئيسية',
    recheckStatus: 'إعادة التحقق من الحالة',
    retry: 'إعادة المحاولة',
    payConfirmAction: 'الإجراءات بعد التحقق من الدفع',
    payCompletedAction: 'الإجراءات بعد إتمام الدفع',
    payFailedAction: 'الإجراءات بعد فشل الدفع',
    payCancelledAction: 'الإجراءات بعد الإلغاء',
    payPendingAction: 'الإجراءات أثناء انتظار الدفع',
  },
  vi: {
    loadingHeader: 'Đang xác minh thanh toán',
    loadingTitle: 'Đang kiểm tra kết quả thanh toán',
    loadingDescription: 'Vui lòng chờ trong giây lát...',
    successHeader: 'Thanh toán hoàn tất',
    successTitle: 'Thanh toán đã hoàn tất',
    successDescription: 'Đơn hàng của bạn đã được tiếp nhận thành công.',
    pendingHeader: 'Thanh toán đang chờ',
    pendingTitle: 'Đang chờ xác nhận thanh toán',
    pendingDescription: 'Cửa hàng đang xác nhận thanh toán. Vui lòng chờ trong giây lát.',
    cancelledHeader: 'Thanh toán đã hủy',
    cancelledTitle: 'Thanh toán đã bị hủy',
    cancelledDescription: 'Thanh toán đã bị hủy. Quay lại trang chủ để thử lại.',
    failedHeader: 'Thanh toán thất bại',
    failedTitle: 'Không thể hoàn tất thanh toán',
    failedRetryHint: 'Vui lòng thử lại hoặc sử dụng phương thức thanh toán khác.',
    invalidPaymentInfo: 'Thông tin thanh toán không hợp lệ.',
    paymentFailedDefault: 'Không thể hoàn tất thanh toán.',
    paymentStatusCheckFailed: 'Không thể kiểm tra trạng thái thanh toán.',
    genericPaymentError: 'Đã xảy ra lỗi khi xử lý thanh toán.',
    orderNumber: 'Mã đơn hàng',
    paymentMethod: 'Phương thức thanh toán',
    paymentAmount: 'Số tiền thanh toán',
    cardPayment: 'Thanh toán bằng thẻ',
    cashPayment: 'Thanh toán tiền mặt',
    bankTransfer: 'Chuyển khoản ngân hàng',
    guestOrderCode: '📋 Mã tra cứu đơn hàng',
    guestOrderCodeDescription: 'Bạn sẽ cần mã này để tra cứu đơn hàng. Hãy lưu lại!',
    copy: 'Sao chép',
    copied: 'Đã sao chép',
    viewReceipt: 'Xem hóa đơn',
    goHome: 'Về trang chủ',
    recheckStatus: 'Kiểm tra lại trạng thái',
    retry: 'Thử lại',
    payConfirmAction: 'Thao tác sau khi xác minh thanh toán',
    payCompletedAction: 'Thao tác sau khi thanh toán',
    payFailedAction: 'Thao tác sau khi thanh toán thất bại',
    payCancelledAction: 'Thao tác sau khi hủy thanh toán',
    payPendingAction: 'Thao tác trong khi chờ thanh toán',
  },
  th: {
    loadingHeader: 'กำลังตรวจสอบการชำระเงิน',
    loadingTitle: 'กำลังตรวจสอบผลการชำระเงิน',
    loadingDescription: 'กรุณารอสักครู่...',
    successHeader: 'ชำระเงินสำเร็จ',
    successTitle: 'การชำระเงินเสร็จสมบูรณ์',
    successDescription: 'ระบบได้รับคำสั่งซื้อของคุณเรียบร้อยแล้ว',
    pendingHeader: 'รอการชำระเงิน',
    pendingTitle: 'กำลังรอการยืนยันการชำระเงิน',
    pendingDescription: 'ร้านค้ากำลังยืนยันการชำระเงิน กรุณารอสักครู่',
    cancelledHeader: 'ยกเลิกการชำระเงิน',
    cancelledTitle: 'การชำระเงินถูกยกเลิก',
    cancelledDescription: 'การชำระเงินถูกยกเลิก กลับไปที่หน้าแรกเพื่อลองใหม่อีกครั้ง',
    failedHeader: 'การชำระเงินล้มเหลว',
    failedTitle: 'ไม่สามารถชำระเงินได้',
    failedRetryHint: 'กรุณาลองใหม่อีกครั้งหรือใช้วิธีการชำระเงินอื่น',
    invalidPaymentInfo: 'ข้อมูลการชำระเงินไม่ถูกต้อง',
    paymentFailedDefault: 'ไม่สามารถชำระเงินได้',
    paymentStatusCheckFailed: 'ไม่สามารถตรวจสอบสถานะการชำระเงินได้',
    genericPaymentError: 'เกิดข้อผิดพลาดระหว่างประมวลผลการชำระเงิน',
    orderNumber: 'หมายเลขคำสั่งซื้อ',
    paymentMethod: 'วิธีการชำระเงิน',
    paymentAmount: 'ยอดชำระ',
    cardPayment: 'ชำระด้วยบัตร',
    cashPayment: 'ชำระด้วยเงินสด',
    bankTransfer: 'โอนผ่านธนาคาร',
    guestOrderCode: '📋 รหัสตรวจสอบคำสั่งซื้อ',
    guestOrderCodeDescription: 'คุณจะต้องใช้รหัสนี้ในการตรวจสอบคำสั่งซื้อ กรุณาบันทึกไว้!',
    copy: 'คัดลอก',
    copied: 'คัดลอกแล้ว',
    viewReceipt: 'ดูใบเสร็จ',
    goHome: 'กลับหน้าแรก',
    recheckStatus: 'ตรวจสอบสถานะอีกครั้ง',
    retry: 'ลองอีกครั้ง',
    payConfirmAction: 'การดำเนินการหลังตรวจสอบการชำระเงิน',
    payCompletedAction: 'การดำเนินการหลังชำระเงินสำเร็จ',
    payFailedAction: 'การดำเนินการหลังชำระเงินล้มเหลว',
    payCancelledAction: 'การดำเนินการหลังยกเลิกการชำระเงิน',
    payPendingAction: 'การดำเนินการระหว่างรอการชำระเงิน',
  },
  nl: {
    loadingHeader: 'Betaling wordt gecontroleerd',
    loadingTitle: 'Het betalingsresultaat wordt gecontroleerd',
    loadingDescription: 'Een ogenblik geduld...',
    successHeader: 'Betaling voltooid',
    successTitle: 'De betaling is voltooid',
    successDescription: 'Uw bestelling is succesvol ontvangen.',
    pendingHeader: 'Betaling in afwachting',
    pendingTitle: 'Wachten op betalingsbevestiging',
    pendingDescription: 'De zaak bevestigt de betaling. Een ogenblik geduld.',
    cancelledHeader: 'Betaling geannuleerd',
    cancelledTitle: 'De betaling is geannuleerd',
    cancelledDescription: 'De betaling is geannuleerd. Ga terug naar de startpagina om het opnieuw te proberen.',
    failedHeader: 'Betaling mislukt',
    failedTitle: 'De betaling kon niet worden voltooid',
    failedRetryHint: 'Probeer het opnieuw of gebruik een andere betaalmethode.',
    invalidPaymentInfo: 'De betalingsgegevens zijn ongeldig.',
    paymentFailedDefault: 'De betaling kon niet worden voltooid.',
    paymentStatusCheckFailed: 'De betalingsstatus kon niet worden gecontroleerd.',
    genericPaymentError: 'Er is een fout opgetreden bij het verwerken van de betaling.',
    orderNumber: 'Bestelnummer',
    paymentMethod: 'Betaalmethode',
    paymentAmount: 'Betaald bedrag',
    cardPayment: 'Kaartbetaling',
    cashPayment: 'Contante betaling',
    bankTransfer: 'Bankoverschrijving',
    guestOrderCode: '📋 Bestelopvraagcode',
    guestOrderCodeDescription: 'U heeft deze code nodig om uw bestelling op te zoeken. Bewaar hem goed!',
    copy: 'Kopiëren',
    copied: 'Gekopieerd',
    viewReceipt: 'Bon bekijken',
    goHome: 'Terug naar start',
    recheckStatus: 'Status opnieuw controleren',
    retry: 'Opnieuw proberen',
    payConfirmAction: 'Acties na betalingscontrole',
    payCompletedAction: 'Acties na de betaling',
    payFailedAction: 'Acties na mislukte betaling',
    payCancelledAction: 'Acties na annulering',
    payPendingAction: 'Acties tijdens betaling in afwachting',
  },
};

type ResultStatus = 'loading' | 'success' | 'failed' | 'pending' | 'cancelled';

interface PaymentResultData {
  orderId: number;
  paymentId: number;
  status: PaymentStatus;
  amount?: string;
  method?: string;
  message?: string;
  // #717: 짧은 순번(#N) 표시용 식별자 — 없으면 orderId 폴백.
  orderNumber?: string | null;
  dailySequence?: number | null;
  membershipRewardPreview?: MembershipRewardPreview | null;
}

function responseStatus(error: unknown): number | null {
  if (typeof error !== 'object' || error === null || !('status' in error)) return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

export function PaymentResultPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resultStatus, setResultStatus] = useState<ResultStatus>('loading');
  const [resultData, setResultData] = useState<PaymentResultData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [billReceipt, setBillReceipt] = useState<SessionBillReceipt | null>(null);
  const [billReceiptLoadState, setBillReceiptLoadState] = useState<'idle' | 'pending' | 'failed'>('idle');
  const sessionChargeContext = useMemo(() => readSessionChargeResult(), []);
  const callbackBundleIdRaw = searchParams.get('bundleId');
  const callbackBundleId = callbackBundleIdRaw === null ? null : Number(callbackBundleIdRaw);
  const isSessionBundleCallback = searchParams.get('source') === '3ds'
    && callbackBundleIdRaw !== null;
  const activeSessionChargeContext = sessionChargeContext !== null
    && Number.isSafeInteger(callbackBundleId)
    && (callbackBundleId ?? 0) > 0
    && callbackBundleId === sessionChargeContext.bundleId
    && isSessionBundleCallback
    ? sessionChargeContext
    : null;
  const effectiveBillReceiptContext = useMemo<PendingBillReceiptContext | null>(() => {
    if (activeSessionChargeContext?.billId !== undefined) {
      return {
        sessionToken: activeSessionChargeContext.sessionToken,
        billId: activeSessionChargeContext.billId,
        deviceToken: activeSessionChargeContext.deviceToken,
      };
    }
    return null;
  }, [activeSessionChargeContext]);

  useEffect(() => {
    if (sessionChargeContext !== null && activeSessionChargeContext === null) {
      clearSessionChargeResult();
    }
  }, [activeSessionChargeContext, sessionChargeContext]);

  const { isLoggedIn } = useAuth();
  const { locale } = useLanguage();
  // #522 — 15언어 전수 정의 → locale 직접 인덱싱 (방어적 es 폴백 유지).
  const t = useMemo(() => RESULT_TRANSLATIONS[locale] ?? RESULT_TRANSLATIONS.es, [locale]);
  const a11y = a11yTranslations[locale];
  const { clearCart } = useCartActions();
  const hasCleared = useRef(false);
  const bundlePollingCountRef = useRef(0);
  const receiptPollingCountRef = useRef(0);
  const bundlePollingInFlightRef = useRef(false);
  const receiptPollingInFlightRef = useRef(false);

  // 비회원용 저장된 비밀번호 (로그인 사용자에게는 표시하지 않음)
  const savedGuestPassword = !isLoggedIn ? getSavedGuestPassword() : null;

  // URL 파라미터에서 결제 정보 추출
  const orderId = searchParams.get('orderId');
  const paymentId = searchParams.get('paymentId');

  /**
   * 3DS 콜백 복귀 outcome 힌트 (#650)
   *
   * admin #1846 이 3DS 콜백 브라우저 복귀를
   * `/payment/result?source=3ds&outcome=declined|invalid` 303 redirect 로 전송한다.
   * (거절 복귀 시 orderId/paymentId 가 빈 값일 수 있음)
   *
   * 서버 권위 원칙: outcome 쿼리는 표시 힌트일 뿐, 최종 상태는 서버 결제상태 조회(SoT).
   * success/pending/cancelled 분기가 먼저 반환되므로 이 힌트는 실패 화면에서만 소비된다
   * — 서버가 paid 라면 outcome=declined 가 있어도 성공 화면이 그대로 노출된다.
   * fail-closed: source≠3ds 또는 미지의 outcome 값이면 null → 기존 동작 유지.
   */
  const threeDsOutcome = useMemo<'declined' | 'invalid' | null>(() => {
    if (searchParams.get('source') !== '3ds') {
      return null;
    }
    const outcome = searchParams.get('outcome');
    return outcome === 'declined' || outcome === 'invalid' ? outcome : null;
  }, [searchParams]);

  // 3DS outcome 안내 문구 (15로케일 — 본문 RESULT_TRANSLATIONS 와 동일하게 locale 인덱싱)
  const t3ds = paymentResult3dsTranslations[locale] ?? paymentResult3dsTranslations.es;

  // 컨텍스트 복귀 네비게이션 문구 (#657 — 15로케일, #650 패턴 정합)
  const tNav = paymentResultNavTranslations[locale] ?? paymentResultNavTranslations.es;

  /**
   * 활성 테이블 세션 복귀 컨텍스트 (#657)
   *
   * 3DS 거절 복귀는 orderId 부재라 상태 재조회가 무의미하고, 기존 '홈으로'는
   * 테이블 세션 복귀 수단을 상실시켰다. tableContext SSOT 유틸로 로컬 보유
   * 컨텍스트(sessionStorage 3키 + localStorage 복구 record)를 판정한다.
   *
   * - 세션 토큰 유효성은 서버 검증 없이 로컬 존재/만료만 판단 — 이동 후
   *   대상 페이지(테이블 메뉴/주문내역)가 자체 검증한다.
   * - fail-safe: 파싱 실패/만료/부재 시 null → 기존 홈 동작 유지.
   */
  const tableReturnContext = useMemo(() => {
    try {
      return getTableSessionReturnContext();
    } catch {
      return null;
    }
  }, []);

  /*
   * 픽업 컨텍스트 복귀는 의도적으로 미지원 — 홈 fail-safe (#658 codex I2).
   *
   * 결제 시도 매장과 바인딩된 로컬 출처가 3DS redirect 를 살아남지 못한다:
   * - orphan record(store_id 보유)는 3DS 경로에서 redirect 직전 clearOrphanOrder()
   *   로 삭제됨 (CheckoutPage '#311: 결제 성공 — orphan record 삭제')
   * - `payment_guest_${orderId}` 는 거절 복귀 시 orderId 부재라 접근 불가
   * - cart(sessionStorage)의 restaurantId 는 "현재 장바구니" 매장일 뿐, 결제했던
   *   매장과의 상관관계가 미검증 (stale URL 재방문 + 매장 전환 시 오이동 위험)
   * → 잘못된 매장 이동보다 보수적인 홈 복귀를 택한다 (테이블 세션은
   *   token-record 일치 + 만료 검증이 있어 컨텍스트 복귀 유지).
   */

  // 푸시 배너 라벨 — checkout i18n 의 pushOptIn* 키 재사용 (이 페이지 사전과 동일한 es 폴백)
  const tPush = checkoutTranslations[locale] ?? checkoutTranslations.es;

  // 비회원 비밀번호는 sessionStorage에서 가져옴 (URL 노출 방지)
  // 결제 생성 시 sessionStorage에 저장됨
  const [guestPassword] = useState(() => {
    if (orderId) {
      const stored = sessionStorage.getItem(`payment_guest_${orderId}`);
      // 사용 후 삭제 (일회성)
      if (stored) {
        sessionStorage.removeItem(`payment_guest_${orderId}`);
      }
      return stored;
    }
    return null;
  });

  /**
   * 비회원 푸시 귀속 컨텍스트 (#694, #695 — 게이트웨이 결제 동선)
   *
   * 카드 플랫폼(Bloomin) 결제는 CheckoutPage → 게이트웨이 → 이 페이지로
   * 리다이렉트되어 OrderSuccessScreen 을 경유하지 않는다. #694 본류는 메모리
   * (컴포넌트 state) 전달이지만 게이트웨이 동선은 풀 리다이렉트로 메모리가
   * 소실되므로 저장분을 사용한다 — 새 영속화는 추가하지 않는다 (이슈 #695).
   *
   * - 테이블 세션(sessionStorage 3키) 생존 시: 세션 단위 귀속 — sessionToken 은
   *   마운트 시점 스냅샷 (#694 P2: 등록 시점 세션 교체/만료 오귀속 차단).
   * - 아니면 주문 단위 귀속: 비번은 **주문별 sessionStorage(payment_guest_)를
   *   우선**하고 localStorage(getSavedGuestPassword)는 폴백 — 결제 상태 조회와
   *   동일한 출처를 써야 조회가 성공한 복귀에서 배너가 누락되지 않는다 (codex P2).
   *   둘 다 없으면 컨텍스트 미생성 → PushOptInBanner 가 자체 숨김 (fail-safe).
   */
  const guestPushContext = useMemo<GuestPushContext | undefined>(() => {
    if (isLoggedIn) return undefined;
    const tableSession = getTableSessionFromStorage();
    if (tableSession) {
      return { kind: 'table-session', sessionToken: tableSession.token };
    }
    const password = guestPassword ?? getSavedGuestPassword();
    if (orderId && password) {
      return { kind: 'order', orderId, guestPassword: password };
    }
    return undefined;
  }, [isLoggedIn, orderId, guestPassword]);

  const { getPaymentStatus, isLoading } = usePaymentApi({
    guestPassword: guestPassword ?? undefined,
  });

  /**
   * 서버 결제상태 조회 가능 조건 — checkPaymentStatus 가드와 단일 출처 (#658 codex I1).
   *
   * getPaymentStatus(orderId, paymentId) 는 두 id 모두 필요하므로 조회 가능
   * 조건은 "둘 다 존재" (기존 가드 `!orderId || !paymentId` 와 동치).
   * 재시도 분기(handleRetry / showFailedRetryButton)도 반드시 이 값을 사용해
   * 가드와 분기 조건이 갈라지는 drift 를 차단한다 — 조회 가능하면 기존
   * '상태 재확인' 경로 우선, 불가할 때만 컨텍스트 네비게이션.
   */
  const canQueryPaymentStatus = Boolean(orderId && paymentId);

  const loadBillReceipt = useCallback(async (
    context: PendingBillReceiptContext,
  ): Promise<boolean> => {
    try {
      const receipt = await splitPaymentApi.getBillReceipt(
        context.sessionToken,
        context.deviceToken,
        context.billId,
      );
      setBillReceipt(receipt);
      setBillReceiptLoadState('idle');
      receiptPollingCountRef.current = 0;
      clearPendingBillReceiptIfMatches(context);
      clearSessionChargeResult();
      setResultData((current) => current === null
        ? current
        : {
            ...current,
            amount: String(receipt.amounts.total),
            membershipRewardPreview: receipt.membership_reward_preview,
          });
      return true;
    } catch (error) {
      const pending = responseStatus(error) === 409;
      setBillReceiptLoadState(pending ? 'pending' : 'failed');
      // 정산 직후 receipt materialization 전 409는 정상적인 짧은 대기 상태다.
      // diagnostics를 오류처럼 오염시키지 않고, 실제 실패만 기록한다.
      if (!pending) logger.warn('[PaymentResultPage] Bill 영수증 조회 실패', error);
      return false;
    }
  }, []);

  // 결제 상태 확인
  const checkPaymentStatus = useCallback(async () => {
    if (isSessionBundleCallback && activeSessionChargeContext === null) {
      setErrorMessage(t.invalidPaymentInfo);
      setResultStatus('failed');
      return;
    }
    if (activeSessionChargeContext !== null) {
      try {
        const response = await splitPaymentApi.getChargeBundleStatus(
          activeSessionChargeContext.sessionToken,
          activeSessionChargeContext.deviceToken,
          activeSessionChargeContext.bundleId,
        );
        const parsedOrderId = orderId === null ? 0 : Number(orderId);
        const parsedPaymentId = paymentId === null ? 0 : Number(paymentId);
        setResultData({
          orderId: Number.isSafeInteger(parsedOrderId) ? parsedOrderId : 0,
          paymentId: Number.isSafeInteger(parsedPaymentId) ? parsedPaymentId : 0,
          status: response.bundle.is_successful ? 'paid' : 'pending',
          amount: response.bundle.total_amount,
          method: 'card',
        });

        if (!response.bundle.polling_complete) {
          setResultStatus('pending');
          return;
        }

        if (response.bundle.is_successful) {
          if (effectiveBillReceiptContext !== null) {
            await loadBillReceipt(effectiveBillReceiptContext);
          } else {
            clearSessionChargeResult();
          }
          setResultStatus('success');
          if (!hasCleared.current) {
            hasCleared.current = true;
            clearCart();
          }
          return;
        }

        if (activeSessionChargeContext.billId !== undefined) {
          clearPendingBillReceiptIfMatches({
            sessionToken: activeSessionChargeContext.sessionToken,
            billId: activeSessionChargeContext.billId,
            deviceToken: activeSessionChargeContext.deviceToken,
          });
        }
        clearSessionChargeResult();
        setErrorMessage(t.paymentFailedDefault);
        setResultStatus('failed');
      } catch (error) {
        logger.error('Session bundle status check failed:', error);
        setErrorMessage(error instanceof Error ? error.message : t.paymentStatusCheckFailed);
        setResultStatus('failed');
      }
      return;
    }

    if (!canQueryPaymentStatus || !orderId || !paymentId) {
      setErrorMessage(t.invalidPaymentInfo);
      setResultStatus('failed');
      return;
    }

    try {
      const response = await getPaymentStatus(
        parseInt(orderId, 10),
        parseInt(paymentId, 10)
      );

      setResultData({
        orderId: parseInt(orderId, 10),
        paymentId: parseInt(paymentId, 10),
        status: response.status,
        amount: response.amount,
        method: response.method,
        orderNumber: response.order_number,
        dailySequence: response.daily_sequence,
        membershipRewardPreview: response.membership_reward_preview,
      });

      // 상태에 따른 결과 설정
      if (response.status === 'paid') {
        if (effectiveBillReceiptContext !== null) {
          await loadBillReceipt(effectiveBillReceiptContext);
        }
        setResultStatus('success');
        // 결제 성공 시 장바구니 초기화 (멱등성 보장)
        if (!hasCleared.current) {
          hasCleared.current = true;
          clearCart();
        }
      } else if (response.status === 'failed') {
        setResultStatus('failed');
        setErrorMessage(response.message || t.paymentFailedDefault);
      } else if (response.status === 'cancelled') {
        setResultStatus('cancelled');
      } else if (response.status === 'pending' || response.status === 'processing') {
        setResultStatus('pending');
      } else {
        setResultStatus('failed');
      }
    } catch (error) {
      logger.error('Payment status check failed:', error);
      setErrorMessage(
        error instanceof Error ? error.message : t.paymentStatusCheckFailed
      );
      setResultStatus('failed');
    }
  }, [
    canQueryPaymentStatus,
    clearCart,
    effectiveBillReceiptContext,
    getPaymentStatus,
    loadBillReceipt,
    orderId,
    paymentId,
    activeSessionChargeContext,
    isSessionBundleCallback,
    t,
  ]);

  // 컴포넌트 마운트 시 결제 상태 확인
  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  useEffect(() => {
    if (activeSessionChargeContext === null || resultStatus !== 'pending') return;
    const timer = window.setInterval(() => {
      if (bundlePollingInFlightRef.current) return;
      if (bundlePollingCountRef.current >= 60) {
        setErrorMessage(t.paymentStatusCheckFailed);
        setResultStatus('failed');
        return;
      }
      bundlePollingCountRef.current += 1;
      bundlePollingInFlightRef.current = true;
      void checkPaymentStatus().finally(() => {
        bundlePollingInFlightRef.current = false;
      });
    }, 3000);
    return () => { window.clearInterval(timer); };
  }, [activeSessionChargeContext, checkPaymentStatus, resultStatus, t.paymentStatusCheckFailed]);

  useEffect(() => {
    if (billReceiptLoadState !== 'pending' || effectiveBillReceiptContext === null) return;
    const timer = window.setInterval(() => {
      if (receiptPollingInFlightRef.current) return;
      if (receiptPollingCountRef.current >= 12) {
        setBillReceiptLoadState('failed');
        return;
      }
      receiptPollingCountRef.current += 1;
      receiptPollingInFlightRef.current = true;
      void loadBillReceipt(effectiveBillReceiptContext).finally(() => {
        receiptPollingInFlightRef.current = false;
      });
    }, 5000);
    return () => { window.clearInterval(timer); };
  }, [billReceiptLoadState, effectiveBillReceiptContext, loadBillReceipt]);

  // 비밀번호 클립보드 복사
  const handleCopyPassword = useCallback(async () => {
    if (!savedGuestPassword) return;

    try {
      await navigator.clipboard.writeText(savedGuestPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (error) {
      logger.error('Clipboard copy failed:', error);
    }
  }, [savedGuestPassword]);

  // 영수증 페이지로 이동
  const handleViewReceipt = () => {
    if (effectiveBillReceiptContext !== null) {
      document.getElementById('bill-receipt')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (orderId) {
      navigate(getReceiptPath(orderId));
    }
  };

  // 홈으로 이동
  const handleGoHome = () => {
    navigate(ROUTES.HOME);
  };

  /**
   * 재시도 (#657)
   *
   * - 조회 가능(canQueryPaymentStatus — checkPaymentStatus 가드와 단일 출처):
   *   기존 동작(서버 상태 재확인) 유지 — 무회귀.
   * - 조회 불가 + 활성 테이블 세션: 상태 재조회는 조회 대상이 없어 무의미
   *   → 주문내역(/order-history, 세션 '전체 결제' 재시도 진입점)으로 이동.
   * - 둘 다 없으면 버튼 자체를 숨긴다 (showFailedRetryButton).
   */
  const showFailedRetryButton = activeSessionChargeContext !== null
    || canQueryPaymentStatus
    || tableReturnContext !== null;

  const handleRetry = () => {
    if (activeSessionChargeContext !== null) {
      setResultStatus('loading');
      setErrorMessage(null);
      void checkPaymentStatus();
      return;
    }
    if (!canQueryPaymentStatus && tableReturnContext) {
      navigate(ROUTES.ORDER_HISTORY);
      return;
    }
    setResultStatus('loading');
    setErrorMessage(null);
    checkPaymentStatus();
  };

  /**
   * 실패 화면 복귀 버튼 (#657 — 컨텍스트 복귀)
   *
   * 활성 테이블 세션 → 해당 매장 테이블 경로, 그 외 기존 홈(fail-safe).
   * 픽업은 결제 매장과 바인딩된 출처 부재로 홈 다운그레이드 (상단 주석, codex I2).
   */
  const failedGoBackLabel = tableReturnContext !== null ? tNav.returnToStore : t.goHome;

  const handleFailedGoBack = () => {
    if (tableReturnContext) {
      navigate(
        getRestaurantTablePath(String(tableReturnContext.storeId), tableReturnContext.tableNumber)
      );
      return;
    }
    navigate(ROUTES.HOME);
  };

  // 로딩 상태
  if (resultStatus === 'loading' || isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col" role="main" aria-busy="true">
        {/* Header */}
        <header className="bg-primary h-[50px] flex items-center justify-center">
          <h1 className="font-bold text-[18px] text-white">{t.loadingHeader}</h1>
        </header>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-6" aria-live="polite">
          <Loader2 className="w-16 h-16 animate-spin text-primary mb-6" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {t.loadingTitle}
          </h2>
          <p className="text-muted-foreground text-center">
            {t.loadingDescription}
          </p>
        </div>
      </div>
    );
  }

  // 성공 상태
  if (resultStatus === 'success') {
    return (
      <div className="min-h-screen bg-background flex flex-col" role="main">
        {/* Header */}
        <header className="bg-primary h-[50px] flex items-center justify-center">
          <h1 className="font-bold text-[18px] text-white">{t.successHeader}</h1>
        </header>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-6" role="alert" aria-live="polite">
          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mb-6" aria-hidden="true">
            <Check className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-2">
            {t.successTitle}
          </h2>
          <p className="text-muted-foreground text-center mb-8">
            {t.successDescription}
          </p>

          {/* 결제 정보 */}
          {resultData && (
            <Card className="w-full max-w-md bg-muted p-4 space-y-3 mb-4">
              {activeSessionChargeContext === null ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.orderNumber}</span>
                  <span className="font-medium">{activeOrderDisplayNumber({ id: resultData.orderId, daily_sequence: resultData.dailySequence, order_number: resultData.orderNumber })}</span>
                </div>
              ) : null}
              {resultData.method && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.paymentMethod}</span>
                  <span className="font-medium">
                    {resultData.method === 'card'
                      ? t.cardPayment
                      : resultData.method === 'cash'
                      ? t.cashPayment
                      : resultData.method}
                  </span>
                </div>
              )}
              {resultData.amount && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.paymentAmount}</span>
                  <span className="font-bold text-primary" data-testid="payment-result-amount">
                    ${parseFloat(resultData.amount).toLocaleString()}
                  </span>
                </div>
              )}
            </Card>
          )}

          {billReceipt !== null ? (
            <Card
              id="bill-receipt"
              className="mb-4 w-full max-w-md space-y-3 bg-muted p-4"
              data-testid="bill-receipt"
            >
              <ul className="space-y-2">
                {billReceipt.items.map((item) => (
                  <li
                    key={`${String(item.order_id)}:${String(item.order_item_id)}`}
                    className="flex items-start justify-between gap-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{item.name}</span>
                      <span
                        className="block text-xs text-muted-foreground"
                        data-testid={`bill-receipt-share-${String(item.order_item_id)}`}
                      >
                        {locale === 'es' ? 'Participación' : locale === 'ko' ? '지분' : 'Share'}:{' '}
                        {(item.share_ppm / 10000).toLocaleString(locale, {
                          maximumFractionDigits: 2,
                        })}%
                      </span>
                    </span>
                    <span className="tabular-nums">
                      {new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency: billReceipt.amounts.currency,
                      }).format(item.amount)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between border-t border-border pt-2 font-semibold">
                <span>{t.paymentAmount}</span>
                <span className="tabular-nums">
                  {new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: billReceipt.amounts.currency,
                  }).format(billReceipt.amounts.total)}
                </span>
              </div>
              {billReceipt.amounts.tip > 0 ? (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{locale === 'es' ? 'Propina' : locale === 'ko' ? '팁' : 'Tip'}</span>
                  <span className="tabular-nums" data-testid="bill-receipt-tip">
                    {new Intl.NumberFormat(locale, {
                      style: 'currency',
                      currency: billReceipt.amounts.currency,
                    }).format(billReceipt.amounts.tip)}
                  </span>
                </div>
              ) : null}
              {billReceipt.amounts.paid > 0 ? (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{locale === 'es' ? 'Pagado' : locale === 'ko' ? '결제됨' : 'Paid'}</span>
                  <span className="tabular-nums" data-testid="bill-receipt-paid">
                    {new Intl.NumberFormat(locale, {
                      style: 'currency',
                      currency: billReceipt.amounts.currency,
                    }).format(billReceipt.amounts.paid)}
                  </span>
                </div>
              ) : null}
              {billReceipt.amounts.refunded > 0 ? (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{locale === 'es' ? 'Reembolsado' : locale === 'ko' ? '환불' : 'Refunded'}</span>
                  <span className="tabular-nums">
                    {new Intl.NumberFormat(locale, {
                      style: 'currency',
                      currency: billReceipt.amounts.currency,
                    }).format(billReceipt.amounts.refunded)}
                  </span>
                </div>
              ) : null}
            </Card>
          ) : null}

          {effectiveBillReceiptContext !== null
          && billReceipt === null
          && billReceiptLoadState !== 'idle' ? (
            <section
              className="mb-4 w-full max-w-md rounded-lg border border-border bg-muted p-4 text-sm"
              role={billReceiptLoadState === 'failed' ? 'alert' : 'status'}
              data-testid="bill-receipt-retry-state"
            >
              <p>
                {billReceiptLoadState === 'pending'
                  ? locale === 'es'
                    ? 'El pago está confirmado. Estamos esperando que el recibo quede listo.'
                    : locale === 'ko'
                      ? '결제는 확인되었습니다. 영수증이 준비되기를 기다리고 있습니다.'
                      : 'Payment is confirmed. We are waiting for the receipt to be ready.'
                  : locale === 'es'
                    ? 'No pudimos cargar el recibo. Puedes intentarlo de nuevo.'
                    : locale === 'ko'
                      ? '영수증을 불러오지 못했습니다. 다시 시도해 주세요.'
                      : 'We could not load the receipt. You can try again.'}
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-3 w-full"
                onClick={() => { void loadBillReceipt(effectiveBillReceiptContext); }}
              >
                {t.retry}
              </Button>
            </section>
          ) : null}

          <GuestPointsIncentive
            preview={effectiveBillReceiptContext === null
              ? resultData?.membershipRewardPreview
              : billReceipt?.membership_reward_preview}
            isLoggedIn={isLoggedIn}
            locale={locale}
            onJoin={() => {
              navigate(`${ROUTES.LOGIN}?returnTo=${encodeURIComponent(ROUTES.HOME)}`);
            }}
          />

          {/* 비회원 주문 조회 코드 (비로그인 사용자에게만 표시) */}
          {savedGuestPassword && (
            <Card className="w-full max-w-md bg-amber-50 border-amber-200 p-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-amber-800 font-semibold text-sm">
                  {t.guestOrderCode}
                </span>
              </div>
              <p className="text-amber-700 text-xs mb-3">
                {t.guestOrderCodeDescription}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-amber-300 rounded px-3 py-2 font-mono text-lg tracking-wider text-center">
                  {savedGuestPassword}
                </code>
                <Button
                  onClick={handleCopyPassword}
                  variant="outline"
                  size="sm"
                  className={`shrink-0 ${passwordCopied ? 'bg-green-100 border-green-500 text-green-700' : 'border-amber-300 text-amber-700 hover:bg-amber-100'}`}
                >
                  {passwordCopied ? (
                    <CheckCheck className="w-4 h-4" aria-hidden="true" />
                  ) : (
                    <Copy className="w-4 h-4" aria-hidden="true" />
                  )}
                  <span className="ml-1">{passwordCopied ? t.copied : t.copy}</span>
                </Button>
              </div>
            </Card>
          )}

          {/* #694/#695: 푸시 옵트인 배너 — 게이트웨이 결제는 OrderSuccessScreen 을 경유하지
              않으므로 여기서 노출한다. 로그인 사용자는 배너의 기존 계정 경로가 자체 분기,
              비회원은 guestPushContext 부재 시 배너가 자체 숨김. 이 페이지에는 자동 이동
              카운트다운이 없어 onInteractionChange 는 전달하지 않는다. */}
          {/* 래퍼에 margin 을 두지 않는다 — 배너 자체 숨김 시 빈 div 가 유령 여백을 만들지
              않도록 (노출 시 간격은 배너 내장 marginTop 이 담당, OrderSuccessScreen 동일). */}
          <div className="w-full max-w-md">
            <PushOptInBanner
              labels={{
                title: tPush.pushOptInTitle,
                accept: tPush.pushOptInAccept,
                later: tPush.pushOptInLater,
                guestTitle: tPush.pushOptInGuestTitle,
                loginHint: tPush.pushOptInLoginHint,
              }}
              guestContext={guestPushContext}
            />
          </div>

          {/* 버튼 */}
          <nav className="w-full max-w-md space-y-3" aria-label={a11y.paymentResultCompleteActions}>
            <Button
              onClick={handleViewReceipt}
              className="w-full bg-primary hover:bg-primary-hover text-white h-12 font-semibold"
            >
              <Receipt className="w-5 h-5 mr-2" aria-hidden="true" />
              {t.viewReceipt}
            </Button>
            <Button
              onClick={handleGoHome}
              variant="outline"
              className="w-full h-12 font-semibold"
            >
              <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
              {t.goHome}
            </Button>
          </nav>
        </div>
      </div>
    );
  }

  // 대기 상태 (현금/이체 결제)
  if (resultStatus === 'pending') {
    return (
      <div className="min-h-screen bg-background flex flex-col" role="main" aria-busy="true">
        {/* Header */}
        <header className="bg-warning h-[50px] flex items-center justify-center">
          <h1 className="font-bold text-[18px] text-white">{t.pendingHeader}</h1>
        </header>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-6" aria-live="polite">
          <div className="w-20 h-20 bg-warning rounded-full flex items-center justify-center mb-6" aria-hidden="true">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-2">
            {t.pendingTitle}
          </h2>
          <p className="text-muted-foreground text-center mb-8">
            {t.pendingDescription}
          </p>

          {/* 결제 정보 */}
          {resultData && (
            <Card className="w-full max-w-md bg-warning/10 border-warning/20 p-4 space-y-3 mb-8">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t.orderNumber}</span>
                <span className="font-medium">{activeOrderDisplayNumber({ id: resultData.orderId, daily_sequence: resultData.dailySequence, order_number: resultData.orderNumber })}</span>
              </div>
              {resultData.method && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.paymentMethod}</span>
                  <span className="font-medium">
                    {resultData.method === 'cash'
                      ? t.cashPayment
                      : resultData.method === 'transfer'
                      ? t.bankTransfer
                      : resultData.method}
                  </span>
                </div>
              )}
              {resultData.amount && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t.paymentAmount}</span>
                  <span className="font-bold text-warning">
                    ${parseFloat(resultData.amount).toLocaleString()}
                  </span>
                </div>
              )}
            </Card>
          )}

          {/* 버튼 */}
          <nav className="w-full max-w-md space-y-3" aria-label={a11y.paymentResultPendingActions}>
            <Button
              onClick={handleRetry}
              className="w-full bg-warning hover:bg-warning/90 text-white h-12 font-semibold"
            >
              <RefreshCcw className="w-5 h-5 mr-2" aria-hidden="true" />
              {t.recheckStatus}
            </Button>
            <Button
              onClick={handleGoHome}
              variant="outline"
              className="w-full h-12 font-semibold"
            >
              <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
              {t.goHome}
            </Button>
          </nav>
        </div>
      </div>
    );
  }

  // 취소 상태
  if (resultStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-background flex flex-col" role="main">
        {/* Header */}
        <header className="bg-muted-foreground h-[50px] flex items-center justify-center">
          <h1 className="font-bold text-[18px] text-white">{t.cancelledHeader}</h1>
        </header>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-6" role="alert" aria-live="assertive">
          <div className="w-20 h-20 bg-muted-foreground rounded-full flex items-center justify-center mb-6" aria-hidden="true">
            <X className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-2">
            {t.cancelledTitle}
          </h2>
          <p className="text-muted-foreground text-center mb-8">
            {t.cancelledDescription}
          </p>

          {/* 버튼 */}
          <nav className="w-full max-w-md" aria-label={a11y.paymentResultCancelledActions}>
            <Button
              onClick={handleGoHome}
              className="w-full bg-primary hover:bg-primary-hover text-white h-12 font-semibold"
            >
              <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
              {t.goHome}
            </Button>
          </nav>
        </div>
      </div>
    );
  }

  // 실패 상태
  // 3DS outcome 힌트가 있으면 거절(declined)/검증 실패(invalid) 구분 메시지 노출 (#650).
  // 이 분기는 서버 조회가 paid/pending/cancelled 를 반환하지 않은 경우에만 도달하므로
  // outcome 힌트 표시가 서버 권위(SoT)와 충돌하지 않는다.
  const failedTitle = threeDsOutcome
    ? threeDsOutcome === 'declined'
      ? t3ds.declinedTitle
      : t3ds.invalidTitle
    : t.failedTitle;
  const failedDescription = threeDsOutcome
    ? threeDsOutcome === 'declined'
      ? t3ds.declinedDescription
      : t3ds.invalidDescription
    : errorMessage || t.genericPaymentError;
  const failedHint = threeDsOutcome
    ? threeDsOutcome === 'declined'
      ? t3ds.declinedRetryHint
      : t3ds.invalidRetryHint
    : t.failedRetryHint;

  return (
    <div className="min-h-screen bg-background flex flex-col" role="main">
      {/* Header */}
      <header className="bg-destructive h-[50px] flex items-center justify-center">
        <h1 className="font-bold text-[18px] text-white">{t.failedHeader}</h1>
      </header>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6" role="alert" aria-live="assertive">
        <div className="w-20 h-20 bg-destructive rounded-full flex items-center justify-center mb-6" aria-hidden="true">
          <X className="w-10 h-10 text-white" />
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">
          {failedTitle}
        </h2>
        <p className="text-muted-foreground text-center mb-4">
          {failedDescription}
        </p>
        <p className="text-sm text-muted-foreground text-center mb-8">
          {failedHint}
        </p>

        {/* 버튼 (#657 — 재시도는 의미 있는 경로가 있을 때만, 복귀는 컨텍스트 우선) */}
        <nav className="w-full max-w-md space-y-3" aria-label={a11y.paymentResultFailedActions}>
          {showFailedRetryButton && (
            <Button
              onClick={handleRetry}
              className="w-full bg-primary hover:bg-primary-hover text-white h-12 font-semibold"
            >
              <RefreshCcw className="w-5 h-5 mr-2" aria-hidden="true" />
              {t.retry}
            </Button>
          )}
          <Button
            onClick={handleFailedGoBack}
            variant="outline"
            className="w-full h-12 font-semibold"
          >
            <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
            {failedGoBackLabel}
          </Button>
        </nav>
      </div>
    </div>
  );
}

export default PaymentResultPage;
